// zip — a pure ZIP (STORE / no compression) container builder. Bundles a set of
// named byte blobs into one .zip Uint8Array, ready to save as a Blob. STORE
// (method 0) keeps it dependency-free and fully synchronous/pure — testable like
// the other cores, and the same scheme the firmware's soft-AP download-all uses
// (src/zip.cpp). Standard PKZIP layout, all little-endian:
//   [local file header + data]*  +  [central directory header]*  +  end-of-central-dir
// The bytes never compress (BMP/GIF are already small/encoded), so the archive is
// the sum of the file sizes + a little header overhead; a DEFLATE variant could be
// added later via the browser's CompressionStream if downloads get large.

export interface ZipEntry {
  /** The file name inside the archive (e.g. "IMG_0007.bmp"). Treated as ASCII. */
  name: string;
  /** The raw file bytes. */
  bytes: Uint8Array;
}

// CRC-32 (IEEE 802.3, reflected poly 0xEDB88320) — the checksum every ZIP entry
// carries. Table built once at module load.
const CRC_TABLE: Uint32Array = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

/** CRC-32 of a byte run (standard check value for "123456789" is 0xCBF43926). */
export function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    c = CRC_TABLE[(c ^ bytes[i]!) & 0xff]! ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

// A fixed DOS timestamp (1980-01-01 00:00:00) so archives are deterministic — the
// content, not the build time, decides the bytes. DOS date = year-1980<<9|mon<<5|day.
const DOS_TIME = 0;
const DOS_DATE = (0 << 9) | (1 << 5) | 1; // 1980-01-01

function asciiBytes(s: string): Uint8Array {
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i) & 0xff;
  return out;
}

/**
 * Build a STORE-method .zip from the entries, in order. Returns the complete
 * archive bytes. Empty input yields a valid empty archive (just the end-of-central-
 * directory record).
 */
export function buildZip(entries: ZipEntry[]): Uint8Array<ArrayBuffer> {
  const locals: Uint8Array[] = []; // local headers + file data, in order
  const central: Uint8Array[] = []; // central directory headers
  let offset = 0; // running offset of the next local header

  for (const e of entries) {
    const nameBytes = asciiBytes(e.name);
    const crc = crc32(e.bytes);
    const size = e.bytes.length;

    // Local file header (30 bytes + name) then the raw data.
    const lh = new Uint8Array(30 + nameBytes.length);
    const lv = new DataView(lh.buffer);
    lv.setUint32(0, 0x04034b50, true); // "PK\3\4"
    lv.setUint16(4, 20, true); // version needed (2.0)
    lv.setUint16(6, 0, true); // flags
    lv.setUint16(8, 0, true); // method = STORE
    lv.setUint16(10, DOS_TIME, true);
    lv.setUint16(12, DOS_DATE, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, size, true); // compressed size (== size for STORE)
    lv.setUint32(22, size, true); // uncompressed size
    lv.setUint16(26, nameBytes.length, true);
    lv.setUint16(28, 0, true); // extra length
    lh.set(nameBytes, 30);
    locals.push(lh, e.bytes);

    // Central directory header (46 bytes + name).
    const ch = new Uint8Array(46 + nameBytes.length);
    const cv = new DataView(ch.buffer);
    cv.setUint32(0, 0x02014b50, true); // "PK\1\2"
    cv.setUint16(4, 20, true); // version made by
    cv.setUint16(6, 20, true); // version needed
    cv.setUint16(8, 0, true); // flags
    cv.setUint16(10, 0, true); // method
    cv.setUint16(12, DOS_TIME, true);
    cv.setUint16(14, DOS_DATE, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, size, true);
    cv.setUint32(24, size, true);
    cv.setUint16(28, nameBytes.length, true);
    cv.setUint16(30, 0, true); // extra length
    cv.setUint16(32, 0, true); // comment length
    cv.setUint16(34, 0, true); // disk number start
    cv.setUint16(36, 0, true); // internal attrs
    cv.setUint32(38, 0, true); // external attrs
    cv.setUint32(42, offset, true); // local header offset
    ch.set(nameBytes, 46);
    central.push(ch);

    offset += lh.length + size;
  }

  const cdOffset = offset;
  const cdSize = central.reduce((n, c) => n + c.length, 0);

  // End of central directory record (22 bytes, no comment).
  const eocd = new Uint8Array(22);
  const ev = new DataView(eocd.buffer);
  ev.setUint32(0, 0x06054b50, true); // "PK\5\6"
  ev.setUint16(4, 0, true); // this disk number
  ev.setUint16(6, 0, true); // disk with central dir
  ev.setUint16(8, entries.length, true); // entries on this disk
  ev.setUint16(10, entries.length, true); // total entries
  ev.setUint32(12, cdSize, true);
  ev.setUint32(16, cdOffset, true);
  ev.setUint16(20, 0, true); // comment length

  const pieces = [...locals, ...central, eocd];
  const total = pieces.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let pos = 0;
  for (const p of pieces) {
    out.set(p, pos);
    pos += p.length;
  }
  return out;
}
